import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePagedList, type ListPage } from './usePagedList';
import type { Product } from '../../utils/product/product-types';
import type { useSearchApi } from './useSearchApi';

type SearchApi = ReturnType<typeof useSearchApi>;

/** Most recent ids sent as "already shown" to the tag graph. */
const FALLBACK_EXCLUDE_CAP = 2000;

export interface GridFeedOptions {
  /** The search the grid shows. Its page 1 is put in the list by the page (useBrowseListings, useUrlSearch). */
  searchParams: Record<string, unknown>;
  /** Filters from the drawer: the results are what was asked for, and no more. */
  filtered: boolean;
  /** False while another view (showcase rows) owns load-more. */
  enabled: boolean;
  currentList: Product[];
  replaceCurrentList: (list: Product[], params?: Record<string, unknown>) => void;
  fetchSearchList: SearchApi['fetchSearchList'];
  fetchTagFallbackList: SearchApi['fetchTagFallbackList'];
  pickDataForListing: SearchApi['pickDataForListing'];
}

/** The search a list belongs to, without its page: a new one is a new list. */
export function gridFeedKey(searchParams: Record<string, unknown>): string {
  const { page: _page, ...rest } = searchParams;
  return JSON.stringify(rest, Object.keys(rest).sort());
}

/**
 * The grid under search, browse, home, shop and tag pages, on the shared
 * pager (usePagedList): the search's pages in order, then, when they run dry,
 * tag-graph recommendations from what is on screen until the graph is dry
 * ("recommend forever"). Filtered results end with the last real page:
 * topping them up put 32 unfiltered listings under one XXS result (staging,
 * 2026-10-09).
 */
export function useGridFeed({
  searchParams,
  filtered,
  enabled,
  currentList,
  replaceCurrentList,
  fetchSearchList,
  fetchTagFallbackList,
  pickDataForListing,
}: GridFeedOptions) {
  const listKey = gridFeedKey(searchParams);
  const inFallbackRef = useRef(false);
  const seenTagsRef = useRef<string[]>([]);
  const latest = useRef({ searchParams, filtered, list: currentList });
  useEffect(() => {
    latest.current = { searchParams, filtered, list: currentList };
  });
  // A new search starts on its own pages, not on the last one's recommendations.
  useEffect(() => {
    inFallbackRef.current = false;
    seenTagsRef.current = [];
  }, [listKey]);

  // pickDataForListing yields the reshaped search row; Product is the UI's view
  // of that same wire object (contexts/ProductProvider narrows the same boundary).
  const asProducts = useCallback(
    (rows: Parameters<SearchApi['pickDataForListing']>[0]) => pickDataForListing(rows, 32) as unknown as Product[],
    [pickDataForListing],
  );

  const fetchPage = useCallback(async (page: number): Promise<ListPage<Product>> => {
    if (!inFallbackRef.current) {
      const rows = await fetchSearchList({ ...latest.current.searchParams, page } as Parameters<SearchApi['fetchSearchList']>[0]);
      if (rows.length > 0) return { items: asProducts(rows), hasMore: true };
      if (latest.current.filtered) return { items: [], hasMore: false };
      inFallbackRef.current = true;
    }

    const shown = latest.current.list;
    if (shown.length === 0) return { items: [], hasMore: false };
    const result = await fetchTagFallbackList({
      currentListTags: Array.from(new Set(shown.flatMap((item) => (item as { tags?: string[] }).tags || []))),
      // Most recent last: past the cap, the newest-shown are the ones kept.
      excludeListingIds: shown.map((item) => Number(item.id)).filter(Number.isFinite).slice(-FALLBACK_EXCLUDE_CAP),
      seenTags: seenTagsRef.current,
      limit: 32,
    });
    seenTagsRef.current = result.seenTags;
    return { items: asProducts(result.listings), hasMore: result.hasMore && result.listings.length > 0 };
  }, [fetchSearchList, fetchTagFallbackList, asProducts]);

  const store = useMemo(() => ({
    list: currentList,
    setList: (list: Product[], page: number) => replaceCurrentList(list, { ...latest.current.searchParams, page }),
  }), [currentList, replaceCurrentList]);

  return usePagedList<Product>({
    listKey,
    fetchPage,
    // Page 1 is the page's to fetch; the feed takes the pages after it.
    enabled: enabled && currentList.length > 0,
    store,
  });
}
